import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box, Typography, Card, Grid, TextField, Button, Divider,
  Switch, FormControlLabel, Alert, Snackbar, Select, MenuItem,
  FormControl, InputLabel, CircularProgress,
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { API_BASE_URL } from "../../config";
import { useAuth } from "../../context/AuthContext";

function SettingsSection({ title, children }) {
  return (
    <Card elevation={0} sx={{ border: "1px solid #e8e8e8", borderRadius: 2, p: 3, bgcolor: "#fff", mb: 3 }}>
      <Typography fontWeight={700} fontSize="14px" color="#344054" mb={2}>{title}</Typography>
      <Divider sx={{ mb: 2 }} />
      {children}
    </Card>
  );
}

// Strip every non-digit character so a "-" (or "+", "e", ".", etc.) can never
// land in state, regardless of whether the user typed, pasted, dragged, or
// composed the value via IME. Used by the numeric inputs on this page.
const sanitizeDigits = (raw) => (raw ?? "").toString().replace(/\D+/g, "");

// Coerce a digit-only string to a non-negative integer, clamped to an optional
// [min, max] range. Empty input is preserved so the user can clear the field
// while typing.
const toNonNegativeInt = (raw, { min = 0, max } = {}) => {
  const cleaned = sanitizeDigits(raw);
  if (cleaned === "") return "";
  let num = Number(cleaned);
  if (Number.isNaN(num)) return min;
  if (num < min) num = min;
  if (max !== undefined && num > max) num = max;
  return num;
};

export default function PlatformSettingsPage() {
  const { user } = useAuth();
  const platformAdminIdentity = user?.email || user?.username || "";
  const loginIdentity = user?.email || user?.username || "";
  const [snack, setSnack] = useState("");
  const [notifLoading, setNotifLoading] = useState(true);
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifError, setNotifError] = useState("");

  // Defaults — in production these would be loaded from an API
  const [defaultTier, setDefaultTier] = useState("tenant");
  const [defaultCloud, setDefaultCloud] = useState("aws");
  const [defaultSeats, setDefaultSeats] = useState(10);
  const [defaultWarnPct, setDefaultWarnPct] = useState(80);
  const [defaultSessionMins, setDefaultSessionMins] = useState(60);
  const [defaultMFA, setDefaultMFA] = useState(false);
  const [defaultForceReset, setDefaultForceReset] = useState(true);

  // SMTP
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUser, setSmtpUser] = useState("");
  const [smtpPass, setSmtpPass] = useState("");
  const [smtpFrom, setSmtpFrom] = useState("");

  // Notifications (persisted — alert inbox + licence expiry)
  const [alertEmail, setAlertEmail] = useState("");
  const [hasSavedAlertEmail, setHasSavedAlertEmail] = useState(false);
  const [notifyExpiry, setNotifyExpiry] = useState(true);
  const [notifySeat, setNotifySeat]     = useState(true);
  const [expiryDays, setExpiryDays]     = useState(6);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setNotifLoading(true);
      setNotifError("");
      try {
        const res = await axios.get(`${API_BASE_URL}/superadmin/settings/notifications`);
        if (cancelled) return;
        const saved = (res.data?.alert_email || "").trim();
        setHasSavedAlertEmail(Boolean(saved));
        // Prefer DB value; fall back to logged-in Platform Admin only when empty
        setAlertEmail(saved || loginIdentity);
        setNotifyExpiry(res.data?.notify_expiry !== false);
        setExpiryDays(res.data?.expiry_warning_days ?? 6);
      } catch (err) {
        if (!cancelled) {
          setNotifError(err.response?.data?.error || "Failed to load notification settings");
        }
      } finally {
        if (!cancelled) setNotifLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // loginIdentity used only as empty-DB fallback at load time
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If GET finished with no saved inbox and AuthContext user arrived later, prefill once
  useEffect(() => {
    if (notifLoading || hasSavedAlertEmail || !loginIdentity) return;
    setAlertEmail((prev) => prev || loginIdentity);
  }, [notifLoading, hasSavedAlertEmail, loginIdentity]);

  const save = (section) => {
    setSnack(`${section} settings saved`);
  };

  const saveNotifications = async () => {
    const trimmed = alertEmail.trim();
    if (!trimmed) {
      setNotifError("Platform alert email is required");
      return;
    }
    setNotifSaving(true);
    setNotifError("");
    try {
      const res = await axios.put(`${API_BASE_URL}/superadmin/settings/notifications`, {
        alert_email: trimmed,
        notify_expiry: notifyExpiry,
        expiry_warning_days: expiryDays === "" ? 6 : expiryDays,
      });
      const saved = (res.data?.alert_email || trimmed).trim();
      setAlertEmail(saved);
      setHasSavedAlertEmail(Boolean(saved));
      setNotifyExpiry(res.data?.notify_expiry !== false);
      setExpiryDays(res.data?.expiry_warning_days ?? 6);
      setSnack("Notification rules saved");
    } catch (err) {
      setNotifError(err.response?.data?.error || "Failed to save notification settings");
    } finally {
      setNotifSaving(false);
    }
  };

  return (
    <Box sx={{ bgcolor: "#f9fafb", px: 4, py: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={0.5}>Platform Settings</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Global defaults applied to new tenant onboarding and platform behaviour
      </Typography>

      {/* Licence Defaults */}
      <SettingsSection title="Licence Defaults">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Default Tier</InputLabel>
              <Select value={defaultTier} label="Default Tier" onChange={(e) => setDefaultTier(e.target.value)}>
                <MenuItem value="tenant">Single Tenant</MenuItem>
                <MenuItem value="enterprise">Enterprise</MenuItem>
                <MenuItem value="multitenant">Multitenant</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Default Cloud</InputLabel>
              <Select value={defaultCloud} label="Default Cloud" onChange={(e) => setDefaultCloud(e.target.value)}>
                <MenuItem value="aws">AWS</MenuItem>
                <MenuItem value="gcp">GCP</MenuItem>
                <MenuItem value="azure">Azure</MenuItem>
                <MenuItem value="private">Dedicated (legacy)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" fullWidth label="Default Seats" type="text"
              value={defaultSeats}
              onChange={(e) => setDefaultSeats(toNonNegativeInt(e.target.value, { min: 1 }))}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*", min: 1 }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" fullWidth label="Warning Threshold (%)" type="text"
              value={defaultWarnPct}
              onChange={(e) => setDefaultWarnPct(toNonNegativeInt(e.target.value, { min: 1, max: 100 }))}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*", min: 1, max: 100 }} />
          </Grid>
        </Grid>
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button variant="contained" size="small" startIcon={<SaveOutlinedIcon />}
            sx={{ textTransform: "none", bgcolor: "#0094FB", "&:hover": { bgcolor: "#0080e0" } }}
            onClick={() => save("Licence defaults")}>
            Save Licence Defaults
          </Button>
        </Box>
      </SettingsSection>

      {/* Auth Defaults */}
      <SettingsSection title="Authentication Defaults">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField size="small" fullWidth label="Session Timeout (minutes)" type="text"
              value={defaultSessionMins}
              onChange={(e) => setDefaultSessionMins(toNonNegativeInt(e.target.value, { min: 1 }))}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*", min: 1 }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControlLabel
              control={<Switch checked={defaultMFA} onChange={(e) => setDefaultMFA(e.target.checked)} />}
              label="MFA enabled by default"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControlLabel
              control={<Switch checked={defaultForceReset} onChange={(e) => setDefaultForceReset(e.target.checked)} />}
              label="Force password reset on first login"
            />
          </Grid>
        </Grid>
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button variant="contained" size="small" startIcon={<SaveOutlinedIcon />}
            sx={{ textTransform: "none", bgcolor: "#0094FB", "&:hover": { bgcolor: "#0080e0" } }}
            onClick={() => save("Auth defaults")}>
            Save Auth Defaults
          </Button>
        </Box>
      </SettingsSection>

      {/* SMTP */}
      <SettingsSection title="Email (SMTP)">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField size="small" fullWidth label="SMTP Host" value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField size="small" fullWidth label="Port" type="text"
              value={smtpPort}
              onChange={(e) => setSmtpPort(toNonNegativeInt(e.target.value, { min: 1, max: 65535 }))}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*", min: 1, max: 65535 }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" fullWidth label="Username" value={smtpUser} onChange={(e) => setSmtpUser(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" fullWidth label="Password" type="password" value={smtpPass} onChange={(e) => setSmtpPass(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField size="small" fullWidth label="From Address" value={smtpFrom} onChange={(e) => setSmtpFrom(e.target.value)} />
          </Grid>
        </Grid>
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button variant="contained" size="small" startIcon={<SaveOutlinedIcon />}
            sx={{ textTransform: "none", bgcolor: "#0094FB", "&:hover": { bgcolor: "#0080e0" } }}
            onClick={() => save("SMTP")}>
            Save SMTP
          </Button>
        </Box>
      </SettingsSection>

      {/* Notification Rules */}
      <SettingsSection title="Notification Rules">
        {notifError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setNotifError("")}>
            {notifError}
          </Alert>
        )}
        {notifLoading ? (
          <Box display="flex" alignItems="center" gap={1} py={2}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Loading notification settings…</Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Licence expiry reminders and tenant extension requests are sent to the platform alert inbox below (not tenant admins).
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8} md={6}>
                <TextField
                  size="small"
                  fullWidth
                  required
                  label="Platform alert email"
                  type="email"
                  value={alertEmail}
                  onChange={(e) => setAlertEmail(e.target.value)}
                  helperText="Inbox for expiry warnings and extension requests"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={<Switch checked={notifyExpiry} onChange={(e) => setNotifyExpiry(e.target.checked)} />}
                  label="Licence expiry alerts"
                />
              </Grid>
              {notifyExpiry && (
                <Grid item xs={12} sm={6} md={3}>
                  <TextField size="small" fullWidth label="Days before expiry" type="text"
                    value={expiryDays}
                    onChange={(e) => setExpiryDays(toNonNegativeInt(e.target.value, { min: 0 }))}
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*", min: 0 }} />
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={<Switch checked={notifySeat} onChange={(e) => setNotifySeat(e.target.checked)} />}
                  label="Seat threshold alerts"
                />
              </Grid>
            </Grid>
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button variant="contained" size="small" startIcon={<SaveOutlinedIcon />}
                disabled={notifSaving}
                sx={{ textTransform: "none", bgcolor: "#0094FB", "&:hover": { bgcolor: "#0080e0" } }}
                onClick={saveNotifications}>
                {notifSaving ? "Saving…" : "Save Rules"}
              </Button>
            </Box>
          </>
        )}
      </SettingsSection>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={3000}
        onClose={() => setSnack("")}
        message={snack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
